// src/pages/register.jsx  — REFACTORED
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Alert } from '../components/ui';
import MahallaSelector from '../components/MahallaSelector';

export default function RegisterPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [regType, setRegType] = useState('phone'); // 'phone' | 'email'
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', password: '', confirm: '' });
  const [mahalla, setMahalla] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();

  const STEPS = [
    { id: 'phone', label: t("auth.phone") },
    { id: 'name', label: t("auth.firstName") },
    { id: 'mahalla', label: t("auth.mahallaTitle") },
    { id: 'password', label: t("auth.password") },
  ];

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const nextStep = (e) => {
    e.preventDefault();
    setError('');
    if (step === 0) {
      if (regType === 'phone' && !form.phone.trim()) { setError(t("apiErrors.AUTH_LOGIN_ERROR")); return; }
      if (regType === 'email' && !form.email.trim()) { setError(t("apiErrors.AUTH_LOGIN_ERROR")); return; }
    }
    if (step === 1 && !form.firstName.trim()) { setError(t("apiErrors.AUTH_LOGIN_ERROR")); return; }
    if (step === 2 && !mahalla?.id) { setError(t('profileModal.selectMahalla')); return; }
    setStep((s) => s + 1);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 4) { setError(t("securityModal.errors.password")); return; }
    if (form.password !== form.confirm) { setError(t('auth.passwordsMismatch') || 'Parollar mos kelmaydi'); return; }
    try {
      const result = await register({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: regType === 'phone' ? form.phone.trim() : undefined,
        email: regType === 'email' ? form.email.trim() : undefined,
        password: form.password.trim(),
        mahallaId: mahalla.id,
      });
      // Backend OTP/email tasdiqlash yubordi — verify sahifasiga o'tish
      if (!result || result.status === 'PENDING_VERIFICATION') {
        navigate('/verify', { replace: true });
        return;
      }
      // To'liq ro'yxatdan o'tish (token bilan kelsa)
      if (result.role === 'admin') navigate('/dashboard', { replace: true });
      else if (result.role === 'driver') navigate('/driver/dashboard', { replace: true });
      else if (result.role === 'operator') navigate('/operator/dashboard', { replace: true });
      else navigate('/main', { replace: true });
    } catch (err) {
      setError(err.message);
    }
  };

  const stepContent = [
    // Step 0: Phone or Email
    <form key="identifier" onSubmit={nextStep} className="flex flex-col gap-6">
      <div className="text-center">
        <div className="h-20 w-20 bg-[#1e9f6b]/10 rounded-[28px] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-[#1e9f6b]/10">
          <span className="material-symbols-outlined text-[#1e9f6b] text-[36px]">
            {regType === 'phone' ? 'phone_android' : 'alternate_email'}
          </span>
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">
          {regType === 'phone' ? t("auth.phone") : t("auth.email")}
        </h2>
        <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 px-4 opacity-70">
          {regType === 'phone' ? t("auth.sentToPhone") : t("auth.sentToEmail")}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-1.5 border border-slate-100 dark:border-slate-800/50 shadow-sm">
        {[
          { key: 'phone', label: t("auth.phone"), icon: 'phone' },
          { key: 'email', label: t("auth.email"), icon: 'mail' },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              setRegType(tab.key);
              setError('');
            }}
            className={`flex-1 flex items-center justify-center gap-2 h-12 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${
              regType === tab.key
                ? 'bg-white dark:bg-slate-900 text-[#1e9f6b] shadow-md shadow-slate-200/50 dark:shadow-none'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <Input
        label={regType === 'phone' ? t("auth.phone") : t("auth.email")}
        placeholder={regType === 'phone' ? '+998 90 123 45 67' : 'email@example.com'}
        type={regType === 'phone' ? 'tel' : 'email'}
        value={regType === 'phone' ? form.phone : form.email}
        onChange={set(regType === 'phone' ? 'phone' : 'email')}
        icon={regType === 'phone' ? 'phone' : 'mail'}
        autoFocus
        required
        className="h-14 font-black italic shadow-sm"
      />
      <Alert message={error} type="error" />
      <Button type="submit" size="xl" className="h-16 font-black uppercase tracking-[0.2em] text-sm shadow-xl shadow-[#1e9f6b]/30 active:scale-[0.98]">
        {t("auth.submit")}
      </Button>

      <div className="relative flex items-center py-2">
        <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
        <span className="flex-shrink-0 mx-4 text-slate-400 text-[10px] font-black tracking-widest uppercase">{t("auth.orDivider")}</span>
        <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
      </div>

      <button
        type="button"
        onClick={() => window.location.href = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1') + '/auth/google'}
        className="flex items-center justify-center gap-3 w-full h-14 bg-white dark:bg-slate-800 text-slate-700 dark:text-white rounded-xl border border-slate-200 dark:border-slate-700 font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
      >
        <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
        <span>{t("auth.googleRegister")}</span>
      </button>
    </form>,

    // Step 1: Name
    <form key="name" onSubmit={nextStep} className="flex flex-col gap-6">
      <div className="text-center">
        <div className="h-20 w-20 bg-blue-50 dark:bg-blue-900/20 rounded-[28px] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-blue-500/10">
          <span className="material-symbols-outlined text-blue-500 text-[36px]">badge</span>
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">{t("profileModal.personalInfo")}</h2>
        <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 px-4 opacity-70">{t("profileModal.title")}</p>
      </div>
      <div className="space-y-4">
        <Input
          label={t("auth.firstName")}
          placeholder={t("auth.firstName")}
          value={form.firstName}
          onChange={set('firstName')}
          icon="person"
          autoFocus
          required
          className="h-14 font-black italic shadow-sm"
        />
        <Input
          label={t("auth.lastName")}
          placeholder={t("auth.lastName")}
          value={form.lastName}
          onChange={set('lastName')}
          icon="badge"
          className="h-14 font-black italic shadow-sm"
        />
      </div>
      <Alert message={error} type="error" />
      <div className="flex gap-4">
        <Button type="button" variant="secondary" size="md" onClick={() => setStep(0)} className="w-16 h-14 flex-shrink-0 bg-slate-50 border-2 border-slate-100 hover:bg-slate-100">
          <span className="material-symbols-outlined text-[24px]">arrow_back</span>
        </Button>
        <Button type="submit" size="md" className="flex-1 h-14 font-black uppercase tracking-widest text-sm shadow-xl shadow-[#1e9f6b]/20">
          {t("auth.submit")}
        </Button>
      </div>
    </form>,

    // Step 2: Mahalla
    <form key="mahalla" onSubmit={nextStep} className="flex flex-col gap-6">
      <div className="text-center">
        <div className="h-20 w-20 bg-emerald-50 dark:bg-emerald-900/20 rounded-[28px] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-emerald-500/10">
          <span className="material-symbols-outlined text-[#1e9f6b] text-[36px]">location_on</span>
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">{t("auth.mahallaTitle")}</h2>
        <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 px-4 opacity-70">
          {t("auth.mahallaSubtitle")}
        </p>
      </div>
      <MahallaSelector value={mahalla} onChange={setMahalla} required />
      <Alert message={error} type="error" />
      <div className="flex gap-4">
        <Button type="button" variant="secondary" size="md" onClick={() => setStep(1)} className="w-16 h-14 flex-shrink-0 bg-slate-50 border-2 border-slate-100 hover:bg-slate-100">
          <span className="material-symbols-outlined text-[24px]">arrow_back</span>
        </Button>
        <Button type="submit" size="md" className="flex-1 h-14 font-black uppercase tracking-widest text-sm shadow-xl shadow-[#1e9f6b]/20">
          {t("auth.submit")}
        </Button>
      </div>
    </form>,

    // Step 3: Password
    <form key="password" onSubmit={onSubmit} className="flex flex-col gap-6">
      <div className="text-center">
        <div className="h-20 w-20 bg-amber-50 dark:bg-amber-900/20 rounded-[28px] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-amber-500/10">
          <span className="material-symbols-outlined text-amber-500 text-[36px]">lock_reset</span>
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">{t("securityModal.newPassword")}</h2>
        <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 px-4 opacity-70">{t("securityModal.passwordHelp")}</p>
      </div>
      <div className="space-y-4">
        <Input
          label={t("auth.password")}
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={form.password}
          onChange={set('password')}
          icon="key"
          rightIcon={showPassword ? "visibility_off" : "visibility"}
          onRightIconClick={() => setShowPassword(!showPassword)}
          autoFocus
          required
          className="h-14 font-black tracking-widest italic shadow-sm"
        />
        <Input
          label={t("securityModal.newPassword")}
          type={showConfirm ? "text" : "password"}
          placeholder="••••••••"
          value={form.confirm}
          onChange={set('confirm')}
          icon="verified_user"
          rightIcon={showConfirm ? "visibility_off" : "visibility"}
          onRightIconClick={() => setShowConfirm(!showConfirm)}
          required
          className="h-14 font-black tracking-widest italic shadow-sm"
        />
      </div>
      <Alert message={error} type="error" />
      <div className="flex gap-4">
        <Button type="button" variant="secondary" size="md" onClick={() => setStep(2)} className="w-16 h-14 flex-shrink-0 bg-slate-50 border-2 border-slate-100 hover:bg-slate-100">
          <span className="material-symbols-outlined text-[24px]">arrow_back</span>
        </Button>
        <Button type="submit" size="md" loading={isLoading} className="flex-1 h-14 font-black uppercase tracking-widest text-sm shadow-xl shadow-[#1e9f6b]/30">
          {t("common.done")}
        </Button>
      </div>
    </form>,
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 dark:from-[#080f0b] dark:via-[#0c1510] dark:to-[#080f0b] flex items-center justify-center px-4 py-8 overflow-hidden">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-[#1e9f6b]/10 blur-[80px]" />
        <div className="absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full bg-sky-400/8 blur-[80px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <Link to="/login" className="flex items-center gap-4 group">
            <div className="h-14 w-14 bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] rounded-[22px] flex items-center justify-center shadow-2xl shadow-[#1e9f6b]/40 group-hover:scale-110 transition-transform duration-500">
              <span className="material-symbols-outlined text-white text-[28px] fill-1">local_gas_station</span>
            </div>
            <span className="text-3xl font-black text-slate-900 dark:text-white italic tracking-tighter">
              SMART<span className="text-[#1e9f6b]">GAZ</span>
            </span>
          </Link>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-2.5 mb-8 px-4">
          {STEPS.map((s, idx) => (
            <React.Fragment key={s.id}>
              <div className={`flex-1 h-2 rounded-full transition-all duration-700 ease-out ${idx <= step ? 'bg-gradient-to-r from-[#1e9f6b] to-[#0d7a52] shadow-lg shadow-[#1e9f6b]/20' : 'bg-slate-200 dark:bg-slate-800'}`} />
            </React.Fragment>
          ))}
        </div>
        
        <div className="flex items-center justify-between px-6 mb-8">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] italic">
            {t("auth.submit")} — {step + 1}
          </span>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] italic">
            TOTAL — {STEPS.length}
          </span>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800/50 shadow-2xl shadow-slate-900/10 p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[40px] pointer-events-none" />
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              {stepContent[step]}
            </motion.div>
          </AnimatePresence>
        </div>

        <p className="text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-10">
          {t("auth.noAccount")}{' '}
          <Link to="/login" className="text-[#1e9f6b] hover:text-[#0d7a52] transition-colors decoration-2 underline-offset-4 hover:underline ml-1">
            {t("auth.login")}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}

